import { useEffect, useState } from "react";
import ApiService from "../../services/api.service";
import { useRole } from "../../components/shared/RoleContext";
import TeamLogo from "../../components/shared/TeamLogo";
import TeamLogoPicker from "../../components/shared/TeamLogoPicker";
import PlayerModal from "../coach/PlayerModal";
import {
  normalizeTag,
  teamTagErrorCopy,
  affiliationErrorCopy,
} from "../../utils/inviteErrorCopy";
import "../client/ClientLayout.css";
import "./LeaguePages.css";

function NameLeagueGate({ onSaved }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await ApiService.putLeague(name.trim(), "basketball");
      onSaved(name.trim());
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="league-name-gate">
      <h1 className="client-h1">Name your league</h1>
      <p className="client-subtitle">This is how your league shows up to teams and officials.</p>
      <input
        className="client-input"
        placeholder="League name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <button className="client-button" onClick={save} disabled={saving || !name.trim()}>
        {saving ? "Saving…" : "Continue"}
      </button>
      {error && <div className="client-error">{error}</div>}
    </div>
  );
}

function AddTeamModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  // The public PlayerWiki #hashtag. Optional here (unlike POST /coach/teams,
  // where it's required), but a team without one is invisible on
  // playerwiki.org and can never be handed off to a coach later.
  const [tag, setTag] = useState("");
  const [division, setDivision] = useState("");
  const [coachPhone, setCoachPhone] = useState("");
  const [coachFirstName, setCoachFirstName] = useState("");
  const [coachLastName, setCoachLastName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [copiedNotice, setCopiedNotice] = useState(false);

  const canSave = name.trim();

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const cleanTag = normalizeTag(tag);
      const resp = await ApiService.createLeagueTeam({
        name: name.trim(),
        // Omit rather than send "" — the server 400s invalidTag on an empty
        // string, and "no hashtag" is a legitimate state.
        ...(cleanTag ? { tag: cleanTag } : {}),
        ...(division.trim() ? { division: division.trim() } : {}),
        ...(coachPhone.trim() ? {
          coachPhone: coachPhone.trim(),
          coachFirstName: coachFirstName.trim(),
          coachLastName: coachLastName.trim(),
        } : {}),
      });
      if (resp.invite && resp.invite.smsSent === false && resp.invite.inviteUrl) {
        await navigator.clipboard?.writeText(resp.invite.inviteUrl).catch(() => {});
        setCopiedNotice(true);
        setSaving(false);
        return;
      }
      onSaved();
    } catch (err) {
      setError(teamTagErrorCopy(err));
      setSaving(false);
    }
  };

  if (copiedNotice) {
    return (
      <div className="coach-modal-backdrop" onClick={onClose}>
        <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
          <h2 className="client-h2">Couldn't text the coach</h2>
          <p className="client-subtitle">
            The team was created, but the invite link couldn't be delivered by SMS — we copied it to your clipboard instead.
          </p>
          <div className="coach-modal-actions">
            <button className="client-button" onClick={onSaved}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coach-modal-backdrop" onClick={onClose}>
      <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="client-h2">Add Team</h2>
        <div className="coach-modal-fields">
          <input className="client-input" placeholder="Team name" value={name}
            onChange={(e) => setName(e.target.value)} />
          <input className="client-input" placeholder="Hashtag (optional) — e.g. #ravens" value={tag}
            autoCapitalize="none" autoCorrect="off"
            onChange={(e) => setTag(e.target.value)} />
          <input className="client-input" placeholder="Division (optional) — e.g. U12 Gold" value={division}
            maxLength={40}
            onChange={(e) => setDivision(e.target.value)} />
          <input className="client-input" placeholder="Coach phone (optional)" value={coachPhone}
            onChange={(e) => setCoachPhone(e.target.value)} />
          {coachPhone.trim() && (
            <>
              <input className="client-input" placeholder="Coach first name" value={coachFirstName}
                onChange={(e) => setCoachFirstName(e.target.value)} />
              <input className="client-input" placeholder="Coach last name" value={coachLastName}
                onChange={(e) => setCoachLastName(e.target.value)} />
            </>
          )}
        </div>
        <p className="client-subtitle">
          We'll text the coach a link to set up their own login. Leave the phone blank to invite them later.
        </p>
        {error && <div className="client-error">{error}</div>}
        <div className="coach-modal-actions">
          <button className="client-button-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="client-button" onClick={save} disabled={saving || !canSave}>
            {saving ? "Saving…" : "Add Team"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TeamRow({ team, onChanged, onRosterChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [players, setPlayers] = useState(null);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [name, setName] = useState(team.name || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [coachInvite, setCoachInvite] = useState(null);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [invitePhone, setInvitePhone] = useState("");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  // null = closed. { player: null } = adding, { player } = editing.
  const [playerModal, setPlayerModal] = useState(null);

  const loadPlayers = async () => {
    setLoadingPlayers(true);
    try {
      const resp = await ApiService.getLeagueTeamPlayers(team.teamId);
      setPlayers(resp.players || []);
    } catch {
      setPlayers([]);
    } finally {
      setLoadingPlayers(false);
    }
  };

  // Feeds the shared coach PlayerModal the league-side routes instead of the
  // coach ones. teamId rides on all three verbs here (see api.service.js).
  const leaguePlayerApi = {
    create: (payload) => ApiService.createLeagueTeamPlayer(team.teamId, payload),
    update: (playerId, payload) => ApiService.updateLeagueTeamPlayer(team.teamId, playerId, payload),
    archive: (playerId) => ApiService.archiveLeagueTeamPlayer(team.teamId, playerId),
  };

  // After any roster write: refresh this roster AND the parent team list, since
  // the collapsed row's player count comes from GET /league/teams.
  const afterPlayerWrite = async () => {
    setPlayerModal(null);
    await loadPlayers();
    onRosterChanged();
  };

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (!next) return;
    if (players === null) {
      await loadPlayers();
    }
    if (team.inviteStatus === "pending" && coachInvite === null) {
      try {
        const resp = await ApiService.getLeagueInvites("coach");
        setCoachInvite((resp.invites || []).find((inv) => inv.teamId === team.teamId) || null);
      } catch {
        // non-fatal — the row just won't show resend/revoke
      }
    }
  };

  const sendCoachInvite = async () => {
    setInviteBusy(true);
    setError(null);
    try {
      const resp = await ApiService.createLeagueInvite({
        accountType: "coach",
        phoneNumber: invitePhone.trim(),
        firstName: inviteFirstName.trim(),
        lastName: inviteLastName.trim(),
        teamId: team.teamId,
      });
      setShowInviteForm(false);
      if (resp.smsSent === false && resp.inviteUrl) {
        await navigator.clipboard?.writeText(resp.inviteUrl).catch(() => {});
        window.alert("Couldn't text the coach — the invite link was copied to your clipboard instead.");
      }
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setInviteBusy(false);
    }
  };

  const resendCoachInvite = async () => {
    if (!coachInvite) return;
    setInviteBusy(true);
    setError(null);
    try {
      const resp = await ApiService.resendLeagueInvite(coachInvite.inviteId);
      if (resp.smsSent === false && resp.inviteUrl) {
        await navigator.clipboard?.writeText(resp.inviteUrl).catch(() => {});
        window.alert("Couldn't text the coach — the invite link was copied to your clipboard instead.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setInviteBusy(false);
    }
  };

  const revokeCoachInvite = async () => {
    if (!coachInvite || !window.confirm("Revoke this invite?")) return;
    setInviteBusy(true);
    setError(null);
    try {
      await ApiService.revokeLeagueInvite(coachInvite.inviteId);
      setCoachInvite(null);
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setInviteBusy(false);
    }
  };

  const rename = async () => {
    if (!name.trim() || name.trim() === team.name) return;
    setSaving(true);
    setError(null);
    try {
      await ApiService.updateLeagueTeam(team.teamId, { name: name.trim() });
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Presign -> PUT the bytes -> persist logoUrl -> reload the teams list.
  // A league admin can set this on ANY team, including one whose coach
  // hasn't claimed their invite yet (unlike the coach-side picker, which
  // only touches the coach's own teams). Mirrors iOS's
  // LeagueTeamsViewModel.uploadTeamLogo.
  const uploadTeamLogo = async (file) => {
    const { url, imageUrl } = await ApiService.getLeagueTeamLogoUploadUrl(team.teamId);
    const res = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": file.type || "image/jpeg" },
      body: file,
    });
    if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
    await ApiService.updateLeagueTeam(team.teamId, { logoUrl: imageUrl });
    onChanged();
  };

  // A coach-owned team is DETACHED, not deleted — the team, its roster and
  // its stats belong to the coach and survive untouched.
  const detach = async () => {
    if (!window.confirm(`Remove ${team.name} from your league? Their coach keeps the team and its roster.`)) return;
    setSaving(true);
    setError(null);
    try {
      await ApiService.deleteLeagueAffiliation(team.teamId);
      onChanged();
    } catch (err) {
      setError(affiliationErrorCopy(err));
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${team.name}? Teams with scheduled games can't be removed.`)) return;
    setSaving(true);
    setError(null);
    try {
      await ApiService.deleteLeagueTeam(team.teamId);
      onChanged();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  // An AFFILIATED team is coach-owned: its LeagueTeams row lives in the
  // team's OWN partition, not this league's, so every league write against it
  // 403s coachOwnedTeam server-side — rename, logo, roster add/edit, coach
  // invite and delete alike. Only the roster READ is allowed. The row appears
  // here because GET /league/teams unions active affiliations in (tagged
  // `affiliated: true`), so without this branch the page would offer five
  // buttons that all fail. `division` always arrives null on these rows by
  // server design and must not look editable either.
  const affiliated = team.affiliated === true;

  const coachStatusText =
    affiliated ? "Coach-owned"
    : team.inviteStatus === "claimed" ? team.coachEmail
    : team.inviteStatus === "pending" ? "Invite sent"
    : "No coach";

  return (
    <div className="coach-history-card">
      <button className="coach-history-row" onClick={toggle}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <TeamLogo name={team.name} logoUrl={team.logoUrl} size={32} />
          <div>
            <div className="coach-history-matchup">{team.name || "Unnamed Team"}</div>
            <div className="coach-history-date">
              {team.tag ? `#${String(team.tag).toUpperCase()} · ` : ""}
              {coachStatusText} · {team.playerCount ?? 0} player{team.playerCount === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      </button>
      {expanded && (
        <div className="coach-history-detail">
          {!affiliated && (
            <>
              <TeamLogoPicker name={team.name} logoUrl={team.logoUrl} onUpload={uploadTeamLogo} />

              <div className="league-team-rename">
                <input className="client-input" value={name} onChange={(e) => setName(e.target.value)} />
                <button className="client-button-secondary" onClick={rename} disabled={saving}>Save</button>
              </div>
            </>
          )}

          {affiliated ? (
            <p className="client-subtitle">
              This team belongs to its own coach. You schedule their games; they run the roster.
            </p>
          ) : (
          <div className="league-coach-section">
            <p className="client-subtitle">Coach</p>
            {team.inviteStatus === "claimed" ? (
              <div>{team.coachEmail}</div>
            ) : team.inviteStatus === "pending" ? (
              <div className="league-invite-actions">
                <span>{coachInvite?.phoneNumber || "Invite pending"}</span>
                <button className="client-button-secondary" onClick={resendCoachInvite} disabled={inviteBusy || !coachInvite}>Resend</button>
                <button className="league-remove-team" onClick={revokeCoachInvite} disabled={inviteBusy || !coachInvite}>Revoke</button>
              </div>
            ) : showInviteForm ? (
              <div className="coach-modal-fields">
                <input className="client-input" placeholder="Phone number" value={invitePhone}
                  onChange={(e) => setInvitePhone(e.target.value)} />
                <input className="client-input" placeholder="Coach first name" value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)} />
                <input className="client-input" placeholder="Coach last name" value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)} />
                <button className="client-button" onClick={sendCoachInvite} disabled={inviteBusy || !invitePhone.trim()}>
                  {inviteBusy ? "Sending…" : "Send Invite"}
                </button>
              </div>
            ) : (
              <button className="client-button-secondary" onClick={() => setShowInviteForm(true)}>+ Invite Coach</button>
            )}
          </div>
          )}

          <div className="league-roster">
            {loadingPlayers ? (
              <div className="client-loading-row">Loading roster…</div>
            ) : players && players.length > 0 ? (
              players.map((p) => (
                <button
                  key={p.playerId}
                  type="button"
                  className="coach-roster-row"
                  disabled={affiliated}
                  onClick={() => { if (!affiliated) setPlayerModal({ player: p }); }}
                >
                  <span className="coach-roster-number">{p.number || "—"}</span>
                  <span className="coach-roster-name">{`${p.firstName || ""} ${p.lastName || ""}`.trim() || "Unnamed"}</span>
                  <span className="coach-roster-meta">{p.position || ""}</span>
                </button>
              ))
            ) : (
              <div className="client-empty">
                {affiliated
                  ? "Their coach hasn't added players yet."
                  : "No players yet. Add them below, or the coach can build the roster themselves."}
              </div>
            )}
            {!affiliated && (
              <button
                className="client-button-secondary"
                onClick={() => setPlayerModal({ player: null })}
                disabled={loadingPlayers}
              >
                + Add Player
              </button>
            )}
          </div>
          {error && <div className="client-error">{error}</div>}
          {affiliated ? (
            <button className="league-remove-team" onClick={detach} disabled={saving}>Remove from league</button>
          ) : (
            <button className="league-remove-team" onClick={remove} disabled={saving}>Remove Team</button>
          )}

          {playerModal && (
            <PlayerModal
              player={playerModal.player}
              teamId={team.teamId}
              api={leaguePlayerApi}
              onClose={() => setPlayerModal(null)}
              onSaved={afterPlayerWrite}
              onArchived={afterPlayerWrite}
            />
          )}
        </div>
      )}
    </div>
  );
}

// One outstanding invitation. Not a TeamRow: the team is NOT in this league
// yet, so every per-team route would 404 — there is nothing to expand into.
function PendingAffiliationRow({ affiliation, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const cancel = async () => {
    if (!window.confirm(`Cancel the invitation to ${affiliation.teamName || "this team"}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await ApiService.deleteLeagueAffiliation(affiliation.teamId);
      onChanged();
    } catch (err) {
      setError(affiliationErrorCopy(err));
      setBusy(false);
    }
  };

  return (
    <div className="coach-history-card">
      <div className="coach-history-row" style={{ cursor: "default" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <TeamLogo name={affiliation.teamName} logoUrl={affiliation.logoUrl} size={32} />
          <div>
            <div className="coach-history-matchup">{affiliation.teamName || "Unnamed Team"}</div>
            <div className="coach-history-date">
              {affiliation.tag ? `#${String(affiliation.tag).toUpperCase()} · ` : ""}
              Invitation sent — waiting for the coach
            </div>
          </div>
        </div>
        <button className="league-remove-team" onClick={cancel} disabled={busy}>Cancel</button>
      </div>
      {error && <div className="client-error">{error}</div>}
    </div>
  );
}

// INVITE an existing coach-owned team by its public #hashtag.
//
// There is no team SEARCH anywhere in the product — no typeahead route exists
// on the authed API — so this is exact-hashtag entry, and a typo is a clean
// 404 tagNotFound.
function AffiliateTeamModal({ onClose, onSent }) {
  const [tag, setTag] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const send = async () => {
    setSaving(true);
    setError(null);
    try {
      const resp = await ApiService.createLeagueAffiliation(normalizeTag(tag));
      const teamName = resp?.affiliation?.teamName || "their coach";
      setNotice(`Invitation sent — ${teamName} will appear in your league once their coach accepts.`);
      setSaving(false);
    } catch (err) {
      // invitePending is a SUCCESS: the server found an unanswered invitation
      // and re-notified the coach rather than duplicating the row. It is a 409
      // only because there was nothing new to create, and showing it in red
      // would tell the admin their action failed when it did what they wanted.
      if (err?.code === "invitePending") {
        setNotice("Already invited — we sent their coach another reminder.");
        setSaving(false);
        return;
      }
      setError(affiliationErrorCopy(err));
      setSaving(false);
    }
  };

  if (notice) {
    return (
      <div className="coach-modal-backdrop" onClick={onSent}>
        <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
          <h2 className="client-h2">Invitation sent</h2>
          <p className="client-subtitle">{notice}</p>
          <div className="coach-modal-actions">
            <button className="client-button" onClick={onSent}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coach-modal-backdrop" onClick={onClose}>
      <div className="coach-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="client-h2">Add an existing team</h2>
        <div className="coach-modal-fields">
          <input className="client-input" placeholder="#hashtag" value={tag}
            autoCapitalize="none" autoCorrect="off"
            onChange={(e) => setTag(e.target.value)} />
        </div>
        <p className="client-subtitle">
          Ask the coach for their team's #hashtag on polyp.me. We'll send them an
          invitation — they keep running their own roster.
        </p>
        {error && <div className="client-error">{error}</div>}
        <div className="coach-modal-actions">
          <button className="client-button-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="client-button" onClick={send} disabled={saving || !normalizeTag(tag)}>
            {saving ? "Sending…" : "Send Invitation"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LeagueTeamsPage() {
  const { refresh } = useRole();
  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [showAffiliate, setShowAffiliate] = useState(false);
  // ONLY the pending rows are rendered from this: active affiliations are
  // already unioned into `teams` by GET /league/teams (tagged affiliated),
  // so rendering the raw list would show every club team twice.
  const [pendingAffiliations, setPendingAffiliations] = useState([]);

  // `quiet` skips the loading flip. The full-page `if (loading) return …`
  // guard below unmounts every TeamRow, which is fine for a rename or a team
  // removal but wrong for a roster write: those happen INSIDE an expanded row,
  // and collapsing it (plus flashing "Loading…") reads as the app losing your
  // place. Roster callbacks pass quiet: true so the player count refreshes
  // underneath the open panel.
  const load = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const leagueResp = await ApiService.getLeague();
      setLeague(leagueResp.league || null);
      if (leagueResp.league) {
        const teamsResp = await ApiService.getLeagueTeams();
        setTeams(teamsResp.teams || []);
        // Own catch: a failed affiliations read must never blank the teams
        // list, and "no pending invitations" is the safe thing to show.
        const affResp = await ApiService.getLeagueAffiliations().catch(() => null);
        setPendingAffiliations((affResp?.affiliations || []).filter((a) => a.status === "pending"));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="client-loading-row">Loading…</div>;

  if (!league) {
    return (
      <NameLeagueGate
        onSaved={async () => {
          await refresh();
          await load();
        }}
      />
    );
  }

  return (
    <div>
      <div className="coach-page-header">
        <div>
          <h1 className="client-h1">{league.name || "My League"}</h1>
          <p className="client-subtitle">
            {teams.length} team{teams.length === 1 ? "" : "s"}
            {pendingAffiliations.length > 0 ? ` · ${pendingAffiliations.length} pending` : ""}
          </p>
        </div>
        <div className="league-schedule-add-actions">
          <button className="client-button-secondary" onClick={() => setShowAffiliate(true)}>+ Add by #hashtag</button>
          <button className="client-button" onClick={() => setShowAddTeam(true)}>+ Add Team</button>
        </div>
      </div>

      {error && <div className="client-error">{error}</div>}

      {pendingAffiliations.length > 0 && (
        <div className="league-pending-list">
          <p className="client-subtitle">Waiting on the coach</p>
          {pendingAffiliations.map((a) => (
            <PendingAffiliationRow key={a.teamId} affiliation={a} onChanged={load} />
          ))}
        </div>
      )}

      {teams.length === 0 ? (
        <div className="client-empty">No teams yet. Add your first one.</div>
      ) : (
        <div className="coach-history-list">
          {teams.map((t) => (
            <TeamRow
              key={t.teamId}
              team={t}
              onChanged={load}
              onRosterChanged={() => load({ quiet: true })}
            />
          ))}
        </div>
      )}

      {showAddTeam && (
        <AddTeamModal
          onClose={() => setShowAddTeam(false)}
          onSaved={() => { setShowAddTeam(false); load(); }}
        />
      )}

      {showAffiliate && (
        <AffiliateTeamModal
          onClose={() => setShowAffiliate(false)}
          onSent={() => { setShowAffiliate(false); load(); }}
        />
      )}
    </div>
  );
}
